import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';

const overlay = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.6)',
  zIndex: 10000,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const modal = {
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  borderRadius: '12px',
  padding: '28px',
  width: '820px',
  maxWidth: '96vw',
  maxHeight: '92vh',
  overflowY: 'auto',
  color: 'var(--text-primary)',
};

const btn = (bg, disabled = false) => ({
  padding: '8px 18px',
  background: disabled ? 'var(--bg-tertiary)' : bg,
  color: disabled ? 'var(--text-tertiary)' : 'var(--text-inverse)',
  border: 'none',
  borderRadius: '6px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '13px',
  fontWeight: '500',
});

const inputSt = {
  flex: 1,
  padding: '8px 12px',
  background: 'var(--bg-primary)',
  border: '1px solid var(--border-color)',
  borderRadius: '6px',
  color: 'var(--text-primary)',
  fontSize: '13px',
};

const TYPE_DOT_COLORS = {
  text: '#4a9eff', number: '#a855f7', boolean: '#22c55e',
  select: '#f59e0b', multiselect: '#f97316', textarea: '#6b7280',
};

const codeBlock = {
  background: 'var(--bg-primary)',
  border: '1px solid var(--border-color)',
  borderRadius: '6px',
  padding: '12px',
  fontFamily: 'monospace',
  fontSize: '11.5px',
  whiteSpace: 'pre',
  overflowX: 'auto',
  maxHeight: '240px',
  overflowY: 'auto',
};

function PackageCard({ pkg, onImport, importing }) {
  const isImporting = importing === pkg.name;
  return (
    <div style={{
      background: 'var(--bg-primary)',
      border: '1px solid var(--border-color)',
      borderRadius: '8px',
      padding: '14px 16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 600, fontFamily: 'monospace', color: 'var(--accent-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {pkg.name}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
            v{pkg.version}{pkg.author ? ` · ${pkg.author}` : ''}
          </div>
        </div>
        <button
          onClick={() => onImport(pkg.name)}
          disabled={!!importing}
          style={{ ...btn('var(--accent-color)', !!importing), padding: '5px 12px', fontSize: '12px', flexShrink: 0 }}
        >
          {isImporting ? '⏳' : '⬇ Import'}
        </button>
      </div>
      {pkg.description && (
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {pkg.description}
        </div>
      )}
      {pkg.npm_url && (
        <a href={pkg.npm_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: 'var(--text-tertiary)', textDecoration: 'none' }}>
          npm ↗
        </a>
      )}
    </div>
  );
}

export default function N8nCatalogModal({ onClose, onImported }) {
  const [query, setQuery] = useState('');
  const [packages, setPackages] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');

  const [importing, setImporting] = useState(null);
  const [importError, setImportError] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [multipleNodes, setMultipleNodes] = useState(null); // {package_name, nodes[]}
  const [copied, setCopied] = useState(false);

  const PAGE_SIZE = 20;
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const inputRef = useRef(null);

  const search = async (q = query, off = 0) => {
    setSearchLoading(true);
    setSearchError('');
    setImportError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/plugins/n8n-catalog/search`, {
        params: { q, size: PAGE_SIZE, offset: off },
        headers,
      });
      setPackages(res.data.packages);
      setTotal(res.data.total);
      setOffset(off);
    } catch (e) {
      setSearchError(e?.response?.data?.detail || 'Search failed — check your connection.');
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => { search('', 0); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const doImport = async (packageName, nodeName = null) => {
    setImporting(packageName);
    setImportError('');
    setMultipleNodes(null);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/plugins/n8n-catalog/import-package`,
        { package_name: packageName, node_name: nodeName },
        { headers },
      );
      if (res.data.multiple_nodes) {
        setMultipleNodes(res.data);
      } else {
        setImportResult(res.data);
        onImported?.();
      }
    } catch (e) {
      setImportError(e?.response?.data?.detail || 'Import failed.');
    } finally {
      setImporting(null);
    }
  };

  const copy = () => {
    if (importResult?.stub_code) {
      navigator.clipboard.writeText(importResult.stub_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const resetImport = () => {
    setImportResult(null);
    setMultipleNodes(null);
    setImportError('');
  };

  // ── Result view ──────────────────────────────────────────────────────────────
  if (importResult) {
    return (
      <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
        <div style={modal}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ margin: 0, fontSize: '18px' }}>📦 n8n Catalog — Imported</h2>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>

          <div style={{ padding: '12px 16px', borderRadius: '6px', marginBottom: '20px', background: 'var(--success-color)22', color: 'var(--success-color)', border: '1px solid var(--success-color)', fontSize: '13px' }}>
            ✅ {importResult.message}
          </div>

          <div style={{ display: 'flex', gap: '16px', marginBottom: '18px', flexWrap: 'wrap' }}>
            {[
              { label: 'Node ID', value: importResult.node_id },
              { label: 'Display Name', value: importResult.display_name },
              { label: 'Version', value: importResult.version },
              { label: 'Fields', value: importResult.fields_count },
            ].map(({ label, value }) => (
              <div key={label} style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '8px 14px', minWidth: '120px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '2px' }}>{label}</div>
                <div style={{ fontSize: '13px', fontWeight: 600, fontFamily: label === 'Node ID' ? 'monospace' : 'inherit' }}>{value}</div>
              </div>
            ))}
          </div>

          {importResult.fields?.length > 0 && (
            <div style={{ marginBottom: '18px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '8px' }}>
                Generated Fields ({importResult.fields_count})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {importResult.fields.map(f => (
                  <span key={f.name} title={f.description || f.name} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                    padding: '3px 10px', borderRadius: '12px',
                    background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                    fontSize: '12px', color: 'var(--text-primary)',
                  }}>
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: TYPE_DOT_COLORS[f.type] || '#666', flexShrink: 0 }} />
                    {f.label}
                    <span style={{ color: 'var(--text-tertiary)', fontSize: '10px' }}>{f.type}</span>
                    {f.required && <span style={{ color: 'var(--error-color)', fontSize: '10px', fontWeight: 700 }}>*</span>}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginBottom: '18px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                Generated Stub — <code style={{ fontWeight: 400 }}>custom_nodes/{importResult.node_id}/node.py</code>
              </div>
              <button onClick={copy} style={{ ...btn('var(--info-color)'), padding: '4px 12px', fontSize: '12px' }}>
                {copied ? '✅ Copied' : '📋 Copy'}
              </button>
            </div>
            <div style={codeBlock}>{importResult.stub_code}</div>
          </div>

          <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '14px', marginBottom: '18px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
            <strong style={{ color: 'var(--text-primary)' }}>Next steps:</strong>
            <ol style={{ margin: '6px 0 0', paddingLeft: '18px' }}>
              <li>Edit <code>custom_nodes/{importResult.node_id}/node.py</code> — replace each <code>raise NotImplementedError</code> with real API calls.</li>
              <li>In Plugin Governance, click <strong>Reload</strong> on <code>{importResult.node_id}</code> to pick up your edits.</li>
              <li>Set trust to <strong>Trusted</strong> so it runs in-process.</li>
            </ol>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button onClick={resetImport} style={btn('var(--bg-tertiary)')}>← Back to Catalog</button>
            <button onClick={onClose} style={btn('var(--accent-color)')}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Node selection view (package with multiple nodes) ────────────────────────
  if (multipleNodes) {
    return (
      <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
        <div style={{ ...modal, width: '560px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '18px' }}>Select Node to Import</h2>
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                {multipleNodes.package_name} contains {multipleNodes.nodes.length} nodes
              </p>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
            {multipleNodes.nodes.map(n => (
              <div key={n.file} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 16px', borderRadius: '8px',
                background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
              }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600 }}>{n.display_name}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                    {n.fields_count} fields detected
                  </div>
                </div>
                <button
                  onClick={() => doImport(multipleNodes.package_name, n.display_name)}
                  disabled={!!importing}
                  style={{ ...btn('var(--accent-color)', !!importing), padding: '6px 14px', fontSize: '12px' }}
                >
                  {importing ? '⏳' : 'Import'}
                </button>
              </div>
            ))}
          </div>

          {importError && (
            <div style={{ padding: '10px 14px', borderRadius: '6px', marginBottom: '14px', background: 'var(--error-color)22', color: 'var(--error-color)', border: '1px solid var(--error-color)', fontSize: '13px' }}>
              {importError}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={resetImport} style={btn('var(--bg-tertiary)')}>← Back</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Search / catalog view ────────────────────────────────────────────────────
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px' }}>📦 Browse n8n Community Nodes</h2>
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>
              Search npm for community packages · click Import to auto-install
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {/* Search bar */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '18px' }}>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && search(query, 0)}
            placeholder="Search nodes… (e.g. airtable, notion, slack)"
            style={inputSt}
          />
          <button
            onClick={() => search(query, 0)}
            disabled={searchLoading}
            style={btn('var(--accent-color)', searchLoading)}
          >
            {searchLoading ? '⏳' : '🔍 Search'}
          </button>
        </div>

        {/* Errors */}
        {searchError && (
          <div style={{ padding: '10px 14px', borderRadius: '6px', marginBottom: '14px', background: 'var(--error-color)22', color: 'var(--error-color)', border: '1px solid var(--error-color)', fontSize: '13px' }}>
            {searchError}
          </div>
        )}
        {importError && (
          <div style={{ padding: '10px 14px', borderRadius: '6px', marginBottom: '14px', background: 'var(--error-color)22', color: 'var(--error-color)', border: '1px solid var(--error-color)', fontSize: '13px' }}>
            ⚠ {importError}
          </div>
        )}

        {/* Loading skeleton */}
        {searchLoading && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '18px' }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} style={{ height: '90px', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '8px', opacity: 0.5 }} />
            ))}
          </div>
        )}

        {/* Results grid */}
        {!searchLoading && packages.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '18px' }}>
            {packages.map(pkg => (
              <PackageCard
                key={pkg.name}
                pkg={pkg}
                onImport={doImport}
                importing={importing}
              />
            ))}
          </div>
        )}

        {!searchLoading && packages.length === 0 && !searchError && (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)', fontSize: '14px' }}>
            No packages found{query ? ` for "${query}"` : ''}.
          </div>
        )}

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: 'var(--text-tertiary)' }}>
            <span>
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total} packages
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                onClick={() => search(query, offset - PAGE_SIZE)}
                disabled={offset === 0 || searchLoading}
                style={{ ...btn('var(--bg-tertiary)', offset === 0 || searchLoading), padding: '4px 12px', fontSize: '12px' }}
              >
                ← Prev
              </button>
              <span style={{ padding: '4px 8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => search(query, offset + PAGE_SIZE)}
                disabled={offset + PAGE_SIZE >= total || searchLoading}
                style={{ ...btn('var(--bg-tertiary)', offset + PAGE_SIZE >= total || searchLoading), padding: '4px 12px', fontSize: '12px' }}
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
